import { Building2 } from 'lucide-react';
import { Suspense } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { PageLoader } from '@/components/common/page-loader';
import { useAuth } from '@/hooks/use-auth';
import { findActiveModule } from '@/lib/navigation';
import { AlertsBell } from './alerts-bell';
import { AssistantButton } from './assistant-panel';
import { Brand } from './brand';
import { Breadcrumbs } from './breadcrumbs';
import { GlobalSearch } from './global-search';
import { MobileNav } from './mobile-nav';
import { ModuleTabs } from './module-tabs';
import { AppSidebar } from './nav-rail';
import { SupportMenu } from './support-menu';
import { TenantSwitcher } from './tenant-switcher';
import { ThemeToggle } from './theme-toggle';
import { UserMenu } from './user-menu';
import { WorkspaceSwitcher } from './workspace-switcher';

/** Empresa ativa: seletor quando o usuário tem acesso a várias, senão só a identificação. */
function CompanyIdentity() {
  const { tenant, memberships, isSuperAdmin } = useAuth();
  if (memberships.length > 1 || isSuperAdmin) return <TenantSwitcher />;
  return (
    <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
      <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="truncate">{tenant?.tradeName ?? 'LD Gestão'}</span>
    </span>
  );
}

/**
 * Layout principal: sidebar compacta de ícones (desktop), cabeçalho com empresa, busca global,
 * alertas e perfil, abas das seções do módulo atual e — no mobile — navegação inferior.
 */
export function AppShell() {
  const { tenant } = useAuth();
  const { pathname } = useLocation();
  const module = findActiveModule(pathname);
  const hasTabs = Boolean(module?.children?.length);

  return (
    <div className="flex min-h-svh bg-background">
      <a
        href="#conteudo"
        className="sr-only z-50 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Pular para o conteúdo
      </a>
      <AppSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/85">
          <div className="flex h-(--header-height) items-center gap-2 px-3 md:gap-4 md:px-6 lg:px-8">
            <Link to="/dashboard" aria-label="Início" className="shrink-0 md:hidden">
              <Brand logoUrl={tenant?.logoUrl} name={tenant?.tradeName} compact />
            </Link>
            <div className="flex min-w-0 flex-1 items-center gap-4 md:flex-none md:basis-auto">
              <WorkspaceSwitcher syncSelection className="hidden md:inline-flex" />
              <div className="min-w-0 md:max-w-60">
                <CompanyIdentity />
              </div>
            </div>
            {/* Um único componente: campo largo no desktop, ícone no mobile */}
            <div className="flex shrink-0 md:min-w-0 md:flex-1 md:justify-center">
              <GlobalSearch />
            </div>
            <div className="flex shrink-0 items-center gap-0.5 md:gap-1">
              <AssistantButton />
              <div className="hidden md:block">
                <SupportMenu />
              </div>
              <AlertsBell />
              <div className="hidden md:block">
                <ThemeToggle />
              </div>
              <UserMenu />
            </div>
          </div>
          {module && hasTabs ? (
            <div className="flex h-11 items-center gap-4 border-t px-3 md:px-6 lg:px-8">
              <ModuleTabs module={module} className="h-full" />
              <div className="ml-auto hidden shrink-0 xl:block">
                <Breadcrumbs />
              </div>
            </div>
          ) : null}
        </header>
        <main
          id="conteudo"
          tabIndex={-1}
          className="min-w-0 flex-1 overflow-x-clip px-4 pt-5 pb-[calc(var(--mobile-bottom-offset)+1.5rem)] outline-none [&:has([data-mobile-action-bar]):not(:has([data-form-actions]))]:pb-[calc(var(--mobile-bottom-offset)+5.5rem)] md:px-6 md:pt-6 md:pb-8 md:[&:has([data-mobile-action-bar])]:pb-8 lg:px-8"
        >
          <div className="mx-auto w-full max-w-[1600px] space-y-5 md:space-y-6">
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
      <MobileNav />
    </div>
  );
}
